/**
 * Route-specific error fallback components
 * Provides context-specific error messages for different parts of the app
 */
import React from 'react';
import { Link } from 'react-router-dom';

const baseStyles = {
  container: {
    padding: '40px 20px',
    maxWidth: '600px',
    margin: '0 auto',
    textAlign: 'center' as const,
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  emoji: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  title: {
    color: '#d32f2f',
    marginBottom: '12px',
  },
  description: {
    color: '#666',
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  primaryButton: {
    padding: '12px 24px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    textDecoration: 'none',
    display: 'inline-block',
  },
  secondaryButton: {
    padding: '12px 24px',
    backgroundColor: '#fff',
    color: '#1976d2',
    border: '2px solid #1976d2',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    textDecoration: 'none',
    display: 'inline-block',
  },
};

export const DashboardErrorFallback: React.FC = () => (
  <div style={baseStyles.container}>
    <div style={baseStyles.emoji}>📊</div>
    <h2 style={baseStyles.title}>Dashboard Error</h2>
    <p style={baseStyles.description}>
      We couldn&apos;t load the dashboard. This might be due to a data loading issue or a temporary
      problem with the visualization.
    </p>
    <div style={baseStyles.buttonContainer}>
      <button onClick={() => window.location.reload()} style={baseStyles.primaryButton}>
        Reload Dashboard
      </button>
      <Link to="/status" style={baseStyles.secondaryButton}>
        View Status Instead
      </Link>
    </div>
  </div>
);

export const StatusErrorFallback: React.FC = () => (
  <div style={baseStyles.container}>
    <div style={baseStyles.emoji}>📡</div>
    <h2 style={baseStyles.title}>Status View Error</h2>
    <p style={baseStyles.description}>
      Unable to load the status view. There may be an issue retrieving installation data.
    </p>
    <div style={baseStyles.buttonContainer}>
      <button onClick={() => window.location.reload()} style={baseStyles.primaryButton}>
        Retry
      </button>
      <Link to="/" style={baseStyles.secondaryButton}>
        Go to Dashboard
      </Link>
    </div>
  </div>
);

export const AlarmProtocolErrorFallback: React.FC = () => (
  <div style={baseStyles.container}>
    <div style={baseStyles.emoji}>🚨</div>
    <h2 style={baseStyles.title}>Alarm Protocol Error</h2>
    <p style={baseStyles.description}>
      Failed to load the alarm protocol. This could be due to connectivity issues or problems
      loading measurement data.
    </p>
    <div style={baseStyles.buttonContainer}>
      <button onClick={() => window.location.reload()} style={baseStyles.primaryButton}>
        Reload Alarms
      </button>
      <Link to="/" style={baseStyles.secondaryButton}>
        Back to Dashboard
      </Link>
    </div>
  </div>
);

export const DocumentsErrorFallback: React.FC = () => (
  <div style={baseStyles.container}>
    <div style={baseStyles.emoji}>📄</div>
    <h2 style={baseStyles.title}>Documents Error</h2>
    <p style={baseStyles.description}>
      Unable to load documents. Please check your connection and try again.
    </p>
    <div style={baseStyles.buttonContainer}>
      <button onClick={() => window.location.reload()} style={baseStyles.primaryButton}>
        Retry
      </button>
      <Link to="/" style={baseStyles.secondaryButton}>
        Back to Dashboard
      </Link>
    </div>
  </div>
);

export const SettingsErrorFallback: React.FC = () => (
  <div style={baseStyles.container}>
    <div style={baseStyles.emoji}>⚙️</div>
    <h2 style={baseStyles.title}>Settings Error</h2>
    <p style={baseStyles.description}>
      There was a problem loading this settings page. Your data is safe, but we couldn&apos;t
      display this section.
    </p>
    <div style={baseStyles.buttonContainer}>
      <button onClick={() => window.location.reload()} style={baseStyles.primaryButton}>
        Try Again
      </button>
      <Link to="/settings/user" style={baseStyles.secondaryButton}>
        Other Settings
      </Link>
    </div>
  </div>
);
